// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/premium/components/operator_pass/checkout/CheckoutStage.module.css';
import {
	formatOperatorPassDateTime,
	formatOperatorPassEarlyBirdEnd,
	formatOperatorPassLongDate,
} from '@app/features/premium/utils/OperatorPassUtils';
import type {OperatorPassStateResponse} from '@fluxer/schema/src/domains/operator_pass/OperatorPassSchemas';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';

const PER_PASS_DESCRIPTOR = msg({
	message: '{count} × {price}, including tax, paid once.',
	comment:
		'Note under the Operator Pass total when buying more than one pass. {count} is the number of passes, 2 or more, and {price} the price of one pass with currency, including tax. The passes are paid once and never renew.',
});
const SINGLE_PASS_PRICE_DESCRIPTOR = msg({
	message: 'Including tax, paid once.',
	comment:
		'Note under the Operator Pass price when buying one pass. The price includes tax and is paid once, it never renews.',
});
const EARLY_BIRD_UNTIL_DESCRIPTOR = msg({
	message: 'Early bird price until {date}.',
	comment:
		'Note under the early bird Operator Pass price, saying the last day of the early bird price. {date} is a date such as January 1, 2027.',
});
const EARLY_BIRD_UNTIL_THEN_DESCRIPTOR = msg({
	message: 'Early bird price until {date}, then {standardPrice}.',
	comment:
		'Note under the early bird Operator Pass price, saying the last day of the early bird price and the price after it. {date} is a date such as January 1, 2027, and {standardPrice} is the price with currency for the same number of passes after that date.',
});
const ON_SALE_FROM_DESCRIPTOR = msg({
	message: 'On sale from {saleOpensAt}.',
	comment:
		'Note in the Operator Pass dialog before the pass can be bought. {saleOpensAt} is a date without a time, such as November 1, 2026.',
});

interface OperatorPassPriceBlockProps {
	state: OperatorPassStateResponse;
	count: number;
	formatPrice: (amountMinor: number, currency: string) => string;
}

export function OperatorPassPriceBlock({state, count, formatPrice}: OperatorPassPriceBlockProps) {
	const {i18n} = useLingui();
	const priceRisesAt = state.phase !== 'standard' ? state.price_rises_at : null;
	return (
		<div className={styles.price} data-flx="premium.operator-pass-checkout.price">
			<span className={styles.total} data-flx="premium.operator-pass-checkout.total">
				{formatPrice(state.price_minor * count, state.currency)}
			</span>
			<span className={styles.note} data-flx="premium.operator-pass-checkout.price-note">
				{count > 1
					? i18n._(PER_PASS_DESCRIPTOR, {count, price: formatPrice(state.price_minor, state.currency)})
					: i18n._(SINGLE_PASS_PRICE_DESCRIPTOR)}
			</span>
			{state.phase === 'waitlist' && state.sale_opens_at && (
				<span
					className={styles.note}
					title={formatOperatorPassDateTime(state.sale_opens_at)}
					data-flx="premium.operator-pass-checkout.on-sale-from"
				>
					{i18n._(ON_SALE_FROM_DESCRIPTOR, {saleOpensAt: formatOperatorPassLongDate(state.sale_opens_at)})}
				</span>
			)}
			{priceRisesAt && (
				<span
					className={styles.note}
					title={formatOperatorPassDateTime(priceRisesAt)}
					data-flx="premium.operator-pass-checkout.early-bird-ends"
				>
					{state.standard_price_minor != null
						? i18n._(EARLY_BIRD_UNTIL_THEN_DESCRIPTOR, {
								date: formatOperatorPassEarlyBirdEnd(priceRisesAt),
								standardPrice: formatPrice(state.standard_price_minor * count, state.currency),
							})
						: i18n._(EARLY_BIRD_UNTIL_DESCRIPTOR, {date: formatOperatorPassEarlyBirdEnd(priceRisesAt)})}
				</span>
			)}
		</div>
	);
}
